import * as React from 'react';
import { __ } from 'react-i18n';
import styled from 'styled-components';
import { withTheme } from 'styled-components';
import { rem } from 'polished';
import {
  FlexCol,
  ZlataPriadkaTitle as Title,
  ZlataPriadkaButton as Button,
  ZlataPriadkaInfoText as InfoText,
  ZlataPriadkaAddressesDeliveryList as AddressesDeliveryList,
  ZlataPriadkaAddressChangeForm as AddressChangeForm,
  ZlataPriadkaLoaderWrapper as LoaderWrapper,
  FlexRowCenterVertical,
} from 'eshop-defaults';
import API, { ThenArg } from '../../services/API';
import { InfoType } from 'eshop-defaults/lib/components/Zemplin/General/InfoText';
import {
  IconType,
  SvgIcon,
} from 'eshop-defaults/lib/components/Zemplin/General/SvgIcon';

interface Props {
  userId: number;
  deliveryAddresses: ThenArg<typeof API.getDeliveryAddresses>;
  refreshAddresses: () => void;
  isFetching: boolean;
  theme: any;
}

function reducer(state, { field, value }) {
  return { ...state, [field]: value };
}

function MyDeliveryAddresses({
  userId,
  deliveryAddresses,
  refreshAddresses,
  isFetching,
  theme,
}: Props) {
  const initialState = {
    country: 'Slovensko',
    country_id: 'sk',
    streetNumber: '',
    city: '',
    zip: '',
    street: '',
    name: '',
    surname: '',
    delivery_phone: '',
    isDefault: false,
  };

  const [state, dispatch] = React.useReducer(reducer, initialState);
  const [isSaving, setIsSaving] = React.useState(false);
  const [dataUpdated, setDataUpdated] = React.useState('');
  const [error, setError] = React.useState(null);
  const [currentId, setCurrentId] = React.useState(null);

  const [isAddressValid, setIsAddressValid] = React.useState(false);

  React.useEffect(() => {
    checkAddress();
  });

  const checkAddress = (): void => {
    if (
      street.trim() !== '' &&
      streetNumber.trim() !== '' &&
      city.trim() !== '' &&
      zip.trim() !== '' &&
      country_id.trim() !== ''
    ) {
      setIsAddressValid(true);
    } else {
      setIsAddressValid(false);
    }
  };

  const onChange = e => {
    dispatch({ field: e.target.name, value: e.target.value });
  };

  const { country_id, streetNumber, street, city, zip, isDefault } = state;

  const handleCurrentAddressChange = id => {
    setCurrentId(id);

    const currentAddress = deliveryAddresses.find(a => a.id === id);
    if (currentAddress) {
      const {
        delivery_street,
        delivery_number,
        delivery_zip,
        delivery_city,
        country,
        isDefault,
      } = currentAddress;
      dispatch({ field: 'country_id', value: country });
      dispatch({ field: 'streetNumber', value: delivery_number });
      dispatch({ field: 'city', value: delivery_city });
      dispatch({ field: 'zip', value: delivery_zip });
      dispatch({ field: 'street', value: delivery_street });
      dispatch({ field: 'isDefault', value: isDefault });
    } else {
      dispatch({ field: 'country_id', value: 'sk' });
      dispatch({ field: 'streetNumber', value: '' });
      dispatch({ field: 'city', value: '' });
      dispatch({ field: 'zip', value: '' });
      dispatch({ field: 'street', value: '' });
      dispatch({ field: 'isDefault', value: false });
    }
  };

  const deleteAddress = async (id: number) => {
    setIsSaving(true);
    try {
      if (id) {
        await API.deleteDeliveryAddress(userId, id || -1, {});
      }
      refreshAddresses();
    } catch (e) {
      setError(e);
      setIsSaving(false);
      return;
    }
    setIsSaving(false);
    setCurrentId(null);
    handleCurrentAddressChange(null);
    setDataUpdated(__('Adresa bola úspešne zmazaná'));
  };

  const updateDetails = async () => {
    setIsSaving(true);
    try {
      if (currentId) {
        await API.updateDeliveryAddress(
          userId,
          currentId || -1,
          {},
          {
            ...state,
            delivery_street: street,
            delivery_number: streetNumber,
            delivery_zip: zip,
            delivery_city: city,
            country: country_id,
            isDefault,
          },
        );
        handleCurrentAddressChange(null);
      } else {
        await API.createNewDeliveryAddress(
          userId,
          {},
          {
            ...state,
            delivery_street: street,
            delivery_number: streetNumber,
            delivery_zip: zip,
            delivery_city: city,
            country: country_id === 'sk' ? 'Slovensko' : country_id,
            country_id: country_id,
            isDefault,
          },
        );
        setCurrentId(null);
        handleCurrentAddressChange(null);
      }
      refreshAddresses();
    } catch (e) {
      setError(e);
      setDataUpdated('');
      setIsSaving(false);
      return;
    }
    setError(null);
    setIsSaving(false);
    setDataUpdated(__('Údaje boli úspešne zmenené'));
  };

  return (
    <Wrapper>
      <StyledTitle marginTop={0} marginBottom={32}>
        {__('Moje dodacie adresy')}
      </StyledTitle>
      {isFetching ? (
        <LoaderWrapper height={500} />
      ) : (
        <>
          <DimWrapper dimmed={isSaving}>
            <AddressesDeliveryList
              onAddressClick={handleCurrentAddressChange}
              onAddressDelete={deleteAddress}
              addresses={deliveryAddresses}
              currentId={currentId}
            />

            <AddressChangeForm
              onChange={onChange}
              street={street}
              streetNumber={streetNumber}
              city={city}
              zip={zip}
              isPreffered={isDefault}
              country_id={country_id}
              isNew={currentId === null}
            />
          </DimWrapper>
          {!error && dataUpdated && (
            <InfoText type={InfoType.SUCCESS} info={dataUpdated} />
          )}
          {error && (
            <InfoText
              type={InfoType.ERROR}
              info={__('Pri ukladaní sa vyskytla chyba')}
            />
          )}
          <ButtonWrapper>
            <SaveButton onClick={updateDetails} disabled={!isAddressValid}>
              {isSaving ? __('Ukladám údaje ...') : __('Uložiť')}
            </SaveButton>
            {/* {currentId && (
              <DeleteButton onClick={deleteAddress}>
                <SvgIcon
                  width={16}
                  height={16}
                  marginRight={16}
                  icon={IconType.trash}
                  viewBox="0 0 16 16"
                  alt="Odstrániť adresu"
                  fill={theme.colors.textPrimary}
                  cursor={'pointer'}
                />
                {isSaving ? __('Odstráňujem...') : __('Odstrániť adresu')}
              </DeleteButton>
            )} */}
          </ButtonWrapper>
        </>
      )}
    </Wrapper>
  );
}

const Wrapper = styled(FlexCol)`
  width: 100%;
  /* max-width: ${rem(1048)}; */
  margin-right: 0;
  margin-left: auto;

  ${({ theme }) => theme.mediab.l925`
    padding-top: ${rem(32)};
  `}

  ${({ theme }) => theme.mediab.m580`
    align-items: center;
    justify-content: center;
  `}
`;

const StyledTitle = styled(Title)`
  font-size: ${rem(24)};
  line-height: ${rem(31)};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.black};
  margin-bottom: ${rem(32)};
`;

const DimWrapper = styled(FlexCol)<{ dimmed: boolean }>`
  opacity: ${({ dimmed }) => (dimmed ? 0.4 : 1)};
`;

const ButtonWrapper = styled(FlexRowCenterVertical)`
  flex-flow: row wrap;

  ${({ theme }) => theme.mediab.m580`
    width: 100%;
    align-items: center;
    justify-content: center;
  `}
`;

const SaveButton = styled(Button)`
  max-width: ${rem(320)};
  width: initial;

  ${({ theme }) => theme.mediab.m580`
    width: 100%;
  `}

  &:hover,
  &:focus {
    background: ${({ theme }) => theme.colors.primaryOnHover};
    outline: none;
  }
`;

export default withTheme(MyDeliveryAddresses);
